import {createApp, h} from 'vue';
import {createPinia} from 'pinia';
import {createInertiaApp, router, usePage} from '@inertiajs/vue3';
import {resolvePageComponent} from 'laravel-vite-plugin/inertia-helpers';
import route from 'ziggy-js';
import {configure} from 'vee-validate';
import {localize, setLocale} from '@vee-validate/i18n';
import veeValidateMessages from './lang/vee-validate';
import i18n from '@/lang/vue-18n';
import {defineRules} from '@/rules';
import FloatingVue from 'floating-vue';
import Toast, {PluginOptions, POSITION} from 'vue-toastification';
import {useHistoryStore} from '@/store/history';
import ToastCloseButton from '@/components/ToastCloseButton.vue';
import {createVfm} from 'vue-final-modal';
import * as Sentry from '@sentry/vue';
// @ts-ignore
import moment from 'moment';

import './bootstrap';
import '../css/app.css';
import 'floating-vue/dist/style.css';
import 'vue-toastification/dist/index.css';
import 'vue-final-modal/style.css';
import 'flowbite';
import 'flowbite-datepicker';
import 'moment/dist/locale/sk';
import {errorToast, warnToast} from '@/util';

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'Laravel';

configure({
    generateMessage: localize(veeValidateMessages)
});

defineRules();
moment.locale('sk');

const toastOptions: PluginOptions = {
    position: POSITION.BOTTOM_RIGHT,
    hideProgressBar: true,
    timeout: 4000,
    transition: 'Vue-Toastification__fade',
    closeButton: ToastCloseButton,
    toastClassName: 'tailwind'
};

// noinspection JSIgnoredPromiseFromCall
createInertiaApp(
    {
        title: (title) => `${title} - ${appName}`,
        progress: {
            color: '#4B5563'
        },
        // @ts-ignore
        resolve: (name) => resolvePageComponent(`./pages/${name}.vue`, import.meta.glob('./pages/**/*.vue')),
        setup({el, App, props, plugin}) {
            const pinia = createPinia();
            const vfm = createVfm()
            const vue = createApp({render: () => h(App, props)});

            Sentry.init({
                app: vue,
                dsn: import.meta.env.VITE_SENTRY_VUE_DSN,
                sendDefaultPii: true,
                integrations: [
                    Sentry.replayIntegration({
                        maskAllInputs: false,
                        maskAllText: false,
                        blockAllMedia: false
                    })
                ],
                enableLogs: true,

                replaysSessionSampleRate: 0.1,
                replaysOnErrorSampleRate: 1.0
            });

            pinia.use(Sentry.createSentryPiniaPlugin());
            vue.use(plugin)
                .use(i18n)
                .use(pinia)
                .use(FloatingVue)
                .use(Toast, toastOptions)
                .use(vfm)
                .mount(el);
            vue.config.globalProperties.$route = route;
            setLocale('sk');
            return vue;
        }
    }
);

router.on('finish', (event) => {
    const historyStore = useHistoryStore();
    const props = usePage();
    const samePage = historyStore.lastURL && (historyStore.lastURL.includes('?') || props.url.includes('?')) ? historyStore.lastURL.split('?')[0] === event.detail.visit.url.pathname : false;
    if (samePage) {
        historyStore.replace(props.url);
    } else {
        historyStore.push(props.url);
    }
});

router.on('error', errors => {
    if (errors.detail.errors.toast_message) {
        errorToast(errors.detail.errors.toast_message);
    } else if (errors.detail.errors.toast_warn_message) {
        warnToast(errors.detail.errors.toast_warn_message)
    }
});
